// Localization (ADR-0032). Hand-rolled on purpose: two lookups and a
// placeholder-replace need no dependency. en.ts is the source of truth;
// every other locale is typed against it, so the typecheck gate enforces
// full translation coverage.
import { en, type Messages } from './locales/en.js';
import { sl } from './locales/sl.js';
import { el } from './dom.js';

export type MessageKey = keyof Messages;

export const LOCALES: Record<string, { label: string; messages: Messages }> = {
  en: { label: 'EN', messages: en },
  sl: { label: 'SL', messages: sl },
};

const STORAGE_KEY = 'ft_lang';

function detect(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES[stored]) return stored;
  } catch {
    /* storage may be unavailable; fall through to browser language */
  }
  const nav = (navigator.language || 'en').toLowerCase();
  for (const code of Object.keys(LOCALES)) {
    if (nav.startsWith(code)) return code;
  }
  return 'en';
}

let current = detect();

export function getLocale(): string {
  return current;
}

export function setLocale(code: string): void {
  if (!LOCALES[code] || code === current) return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* best effort */
  }
  current = code;
  // Views read t() at render time; a reload re-renders everything.
  location.reload();
}

export function t(key: MessageKey, params?: Record<string, string | number>): string {
  let msg = LOCALES[current]?.messages[key] ?? en[key];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      msg = msg.split(`{${k}}`).join(String(v));
    }
  }
  return msg;
}

/** Small EN/SL/… switcher, shown on every screen (picker, wizard, header). */
export function langSwitcher(): HTMLElement {
  const wrap = el('span', { class: 'lang-switch' });
  for (const [code, { label }] of Object.entries(LOCALES)) {
    wrap.append(
      el(
        'button',
        {
          class: code === current ? 'lang active' : 'lang',
          type: 'button',
          'data-lang': code,
          onclick: () => setLocale(code),
        },
        label,
      ),
    );
  }
  return wrap;
}
